import { Card, CardContent } from '@/components/ui/card';
import { StatusSummary, PipelineStatus, STATUS_CONFIG } from '../types';
import { cn } from '@/lib/utils';

interface StatusSummaryCardsProps {
  summaries: StatusSummary[];
  selectedStatus: PipelineStatus | 'all';
  onStatusClick: (status: PipelineStatus | 'all') => void;
}

export function StatusSummaryCards({
  summaries,
  selectedStatus,
  onStatusClick
}: StatusSummaryCardsProps) {
  const formatCurrency = (amount: number) => {
    if (amount >= 1_000_000) {
      return `$${(amount / 1_000_000).toFixed(1)}M`;
    }
    if (amount >= 1_000) {
      return `$${(amount / 1_000).toFixed(0)}K`;
    }
    return `$${amount.toFixed(0)}`;
  };

  // Calculate totals for "All" card
  const totalCount = summaries.reduce((sum, s) => sum + s.count, 0);
  const totalAmount = summaries.reduce((sum, s) => sum + s.totalAmount, 0);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-3">
      {/* All Status Card */}
      <Card
        className={cn(
          'cursor-pointer transition-all hover:shadow-md',
          selectedStatus === 'all' && 'ring-2 ring-primary'
        )}
        onClick={() => onStatusClick('all')}
      >
        <CardContent className="p-4">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            All
          </div>
          <div className="mt-1 text-2xl font-bold">{totalCount}</div>
          <div className="text-sm text-muted-foreground">
            {formatCurrency(totalAmount)}
          </div>
        </CardContent>
      </Card>

      {/* Individual Status Cards */}
      {summaries.map((summary) => {
        const config = STATUS_CONFIG[summary.status];
        return (
          <Card
            key={summary.status}
            className={cn(
              'cursor-pointer transition-all hover:shadow-md',
              selectedStatus === summary.status && 'ring-2 ring-primary'
            )}
            onClick={() => onStatusClick(summary.status)}
          >
            <CardContent className="p-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide truncate">
                {config.label}
              </div>
              <div className="mt-1 text-2xl font-bold">{summary.count}</div>
              <div className="text-sm text-muted-foreground">
                {formatCurrency(summary.totalAmount)}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
